import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight, ChevronsUpDown, Filter } from 'lucide-react'
import { cn } from './internal/utils'
import { BORDER } from './internal/presets'
import { buildViewData, nextSortOrder, readCell, resolveRowKey } from './internal/tableLogic'
import Checkbox from './Checkbox'
import Dropdown from './Dropdown'
import Empty from './Empty'
import Pagination from './Pagination'
import Radio from './Radio'
import Spin from './Spin'

const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' }

const columnKeyOf = (column, index) => column.key ?? column.dataIndex ?? index

const FilterMenu = ({ column, selected, onApply }) => {
  const [draft, setDraft] = useState(selected ?? [])

  return (
    <div className="w-48 p-2">
      <div className="max-h-56 space-y-1 overflow-auto">
        {column.filters.map((filter) => (
          <Checkbox
            key={String(filter.value)}
            checked={draft.includes(filter.value)}
            onChange={(event) =>
              setDraft((previous) =>
                event.target.checked
                  ? [...previous, filter.value]
                  : previous.filter((item) => item !== filter.value)
              )
            }
          >
            {filter.text}
          </Checkbox>
        ))}
      </div>
      <div className={cn('mt-2 flex justify-between gap-2 border-t pt-2', BORDER)}>
        <button
          type="button" onClick={() => {
            setDraft([])
            onApply([])
          }}
          className="min-h-[36px] px-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-body)]" >
          Réinitialiser
        </button>
        <button
          type="button" onClick={() => onApply(draft)}
          className="min-h-[36px] rounded-[var(--radius-xs)] bg-[var(--primary-color)] px-3 text-xs font-medium text-white" >
          Filtrer
        </button>
      </div>
    </div>
  )
}

/**
 * antd Table. Supports columns (render, dataIndex, key, sorter, filters, width,
 * align, fixed, ellipsis, colSpan), dataSource, rowKey, pagination (object or
 * false), rowSelection (checkbox and radio), expandable, scroll, summary,
 * loading, bordered, size, onRow and onChange.
 *
 * The `.ui-table-*` class hooks are kept so the brand header styling in
 * index.css keeps applying until Phase 7 removes it.
 */
const Table = ({
  columns = [],
  dataSource = [],
  rowKey = 'key',
  pagination,
  rowSelection,
  expandable,
  loading = false,
  bordered = false,
  size = 'default',
  scroll,
  summary,
  locale,
  onChange,
  onRow,
  rowClassName,
  showHeader = true,
  title,
  footer,
  className,
  style,
  ...rest
}) => {
  const { t } = useTranslation()
  const visibleColumns = useMemo(() => columns.filter(Boolean), [columns])

  const [sorter, setSorter] = useState(() => {
    const preset = visibleColumns.find((column) => column.defaultSortOrder)
    return preset
      ? { columnKey: columnKeyOf(preset, 0), order: preset.defaultSortOrder, column: preset }
      : { columnKey: null, order: null }
  })
  const [filters, setFilters] = useState({})
  const [openFilterKey, setOpenFilterKey] = useState(null)
  const [internalPage, setInternalPage] = useState({ current: 1, pageSize: 10 })
  const [expandedKeys, setExpandedKeys] = useState(expandable?.defaultExpandedRowKeys ?? [])

  const paginationEnabled = pagination !== false
  const resolvedPagination = paginationEnabled
    ? {
        current: pagination?.current ?? internalPage.current,
        pageSize: pagination?.pageSize ?? internalPage.pageSize,
        ...(pagination || {}),
      }
    : false

  const currentPage = resolvedPagination ? resolvedPagination.current : null
  const currentPageSize = resolvedPagination ? resolvedPagination.pageSize : null

  const { rows, total } = useMemo(
    () =>
      buildViewData({
        dataSource,
        columns: visibleColumns,
        filters,
        sorter,
        pagination: paginationEnabled ? { current: currentPage, pageSize: currentPageSize } : false,
      }),
    [
      currentPage,
      currentPageSize,
      dataSource,
      filters,
      paginationEnabled,
      sorter,
      visibleColumns,
    ]
  )

  const emit = (nextPagination, nextFilters, nextSorter) => {
    onChange?.(nextPagination ?? resolvedPagination, nextFilters ?? filters, nextSorter ?? sorter, {
      currentDataSource: dataSource,
    })
  }

  const toggleSort = (column, index) => {
    const key = columnKeyOf(column, index)
    const order = sorter.columnKey === key ? nextSortOrder(sorter.order) : 'ascend'
    const next = { columnKey: key, order, column, field: column.dataIndex }
    setSorter(next)
    emit(undefined, undefined, next)
  }

  const applyFilter = (column, index, values) => {
    const key = columnKeyOf(column, index)
    const next = { ...filters, [key]: values }
    setFilters(next)
    emit(undefined, next, undefined)
  }

  const changePage = (current, pageSize) => {
    const next = { ...resolvedPagination, current, pageSize }
    if (pagination?.current == null) setInternalPage({ current, pageSize })
    pagination?.onChange?.(current, pageSize)
    emit(next, undefined, undefined)
  }

  const selectedKeys = rowSelection?.selectedRowKeys ?? []

  const changeSelection = (keys) => {
    const records = dataSource.filter((record, index) =>
      keys.includes(resolveRowKey(record, index, rowKey))
    )
    rowSelection?.onChange?.(keys, records)
  }

  const toggleRow = (key, record, checked) => {
    const keys = rowSelection?.type === 'radio'
      ? [key]
      : checked
        ? [...selectedKeys, key]
        : selectedKeys.filter((item) => item !== key)
    changeSelection(keys)
    rowSelection?.onSelect?.(record, checked, [])
  }

  const pageKeys = rows.map((record, index) => resolveRowKey(record, index, rowKey))
  const selectableKeys = pageKeys.filter((key, index) => {
    const disabled = rowSelection?.getCheckboxProps?.(rows[index])?.disabled
    return !disabled
  })
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((key) => selectedKeys.includes(key))
  const someSelected = selectableKeys.some((key) => selectedKeys.includes(key))

  const expandableEnabled = Boolean(expandable?.expandedRowRender)
  const resolvedExpandedKeys = expandable?.expandedRowKeys ?? expandedKeys

  const toggleExpanded = (key, record) => {
    const isOpen = resolvedExpandedKeys.includes(key)
    const next = isOpen
      ? resolvedExpandedKeys.filter((item) => item !== key)
      : [...resolvedExpandedKeys, key]
    if (!expandable?.expandedRowKeys) setExpandedKeys(next)
    expandable?.onExpand?.(!isOpen, record)
    expandable?.onExpandedRowsChange?.(next)
  }

  // Nomad: TableHead h-10 px-2; TableCell p-2 (dense small keeps py-1.5)
  const cellPadding = size === 'small' ? 'px-2 py-1.5' : 'p-2'
  const totalColumnCount =
    visibleColumns.length + (rowSelection ? 1 : 0) + (expandableEnabled ? 1 : 0)

  return (
    <div className={cn('ui-table-wrapper', className)} style={style} {...rest}>
      {title ? <div className="ui-table-title px-2 py-3">{title(dataSource)}</div> : null}

      <Spin spinning={Boolean(loading)}>
        <div
          className={cn('ui-table overflow-auto', bordered && `border ${BORDER}`)}
          style={{ maxHeight: scroll?.y, maxWidth: scroll?.x === true ? '100%' : scroll?.x }}
        >
          <table className="ui-table-table w-full border-collapse text-sm">
            {showHeader ? (
              <thead className="ui-table-thead">
                <tr>
                  {expandableEnabled ? <th className={cn('w-10', cellPadding)} /> : null}
                  {rowSelection ? (
                    <th className={cn('ui-table-selection-column w-10', cellPadding)}>
                      {rowSelection.type !== 'radio' ? (
                        <Checkbox
                          checked={allSelected}
                          indeterminate={!allSelected && someSelected}
                          onChange={(event) =>
                            changeSelection(
                              event.target.checked
                                ? [...new Set([...selectedKeys, ...selectableKeys])]
                                : selectedKeys.filter((key) => !selectableKeys.includes(key))
                            )
                          }
                        >
                          <span className="sr-only">{t('common.selectAll')}</span>
                        </Checkbox>
                      ) : null}
                    </th>
                  ) : null}

                  {visibleColumns.map((column, index) => {
                    const key = columnKeyOf(column, index)
                    const isSorted = sorter.columnKey === key && sorter.order
                    const activeFilters = filters[key] ?? []

                    return (
                      <th
                        key={String(key)}
                        scope="col" aria-sort={
                          column.sorter
                            ? isSorted
                              ? sorter.order === 'ascend'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                            : undefined
                        }
                        className={cn(
                          'ui-table-cell font-medium text-[var(--muted-foreground)]',
                          cellPadding,
                          ALIGN[column.align] || ALIGN.left,
                          bordered && `border ${BORDER}`
                        )}
                        style={{ width: column.width, minWidth: column.minWidth }}
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.sorter ? (
                            <button
                              type="button" onClick={() => toggleSort(column, index)}
                              className="inline-flex min-h-[32px] items-center gap-1 text-inherit" >
                              {column.title}
                              <ChevronsUpDown
                                size={12}
                                aria-hidden="true" className={cn(
                                  'ui-table-column-sorter',
                                  isSorted ? 'opacity-100' : 'opacity-40'
                                )}
                              />
                            </button>
                          ) : (
                            column.title
                          )}

                          {column.filters?.length ? (
                            <Dropdown
                              trigger="click" placement="bottomRight" open={openFilterKey === key}
                              onOpenChange={(next) => setOpenFilterKey(next ? key : null)}
                              dropdownRender={() => (
                                <FilterMenu
                                  column={column}
                                  selected={activeFilters}
                                  onApply={(values) => {
                                    applyFilter(column, index, values)
                                    setOpenFilterKey(null)
                                  }}
                                />
                              )}
                            >
                              <button
                                type="button" aria-label={`Filtrer ${typeof column.title === 'string' ? column.title : ''}`}
                                className={cn(
                                  'ui-table-filter-trigger inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-sm)]',
                                  activeFilters.length > 0 && 'text-[var(--primary-color)]'
                                )}
                              >
                                <Filter size={12} aria-hidden="true" />
                              </button>
                            </Dropdown>
                          ) : null}
                        </span>
                      </th>
                    )
                  })}
                </tr>
              </thead>
            ) : null}

            <tbody className="ui-table-tbody">
              {rows.length === 0 ? (
                <tr className="ui-table-placeholder">
                  <td colSpan={totalColumnCount} className="p-0">
                    {locale?.emptyText ?? <Empty />}
                  </td>
                </tr>
              ) : (
                rows.map((record, index) => {
                  const key = resolveRowKey(record, index, rowKey)
                  const rowProps = onRow?.(record, index) ?? {}
                  const isExpanded = resolvedExpandedKeys.includes(key)
                  const checkboxProps = rowSelection?.getCheckboxProps?.(record) ?? {}

                  return [
                    <tr
                      key={String(key)}
                      {...rowProps}
                      className={cn(
                        'ui-table-row border-b',
                        BORDER,
                        typeof rowClassName === 'function'
                          ? rowClassName(record, index)
                          : rowClassName,
                        selectedKeys.includes(key) && 'ui-table-row-selected',
                        rowProps.className
                      )}
                    >
                      {expandableEnabled ? (
                        <td className={cn('ui-table-cell w-10', cellPadding)}>
                          {expandable.rowExpandable?.(record) === false ? null : (
                            <button
                              type="button" aria-expanded={isExpanded}
                              aria-label={isExpanded ? t('common.collapseRow') : t('common.expandRow')}
                              onClick={() => toggleExpanded(key, record)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] hover:bg-[var(--accent)]" >
                              {isExpanded ? (
                                <ChevronDown size={14} aria-hidden="true" />
                              ) : (
                                <ChevronRight size={14} aria-hidden="true" />
                              )}
                            </button>
                          )}
                        </td>
                      ) : null}

                      {rowSelection ? (
                        <td className={cn('ui-table-cell ui-table-selection-column w-10', cellPadding)}>
                          {rowSelection.type === 'radio' ? (
                            <Radio
                              checked={selectedKeys.includes(key)}
                              disabled={checkboxProps.disabled}
                              onChange={() => toggleRow(key, record, true)}
                            >
                              <span className="sr-only">{t('common.selectRow')}</span>
                            </Radio>
                          ) : (
                            <Checkbox
                              checked={selectedKeys.includes(key)}
                              disabled={checkboxProps.disabled}
                              onChange={(event) => toggleRow(key, record, event.target.checked)}
                            >
                              <span className="sr-only">{t('common.selectRow')}</span>
                            </Checkbox>
                          )}
                        </td>
                      ) : null}

                      {visibleColumns.map((column, columnIndex) => {
                        const value = readCell(record, column)
                        const rendered = column.render
                          ? column.render(value, record, index)
                          : value
                        // antd lets render() return { children, props } for spans.
                        const isDescriptor =
                          rendered && typeof rendered === 'object' && 'children' in rendered && 'props' in rendered
                        const cellProps = isDescriptor ? rendered.props : {}
                        if (cellProps.colSpan === 0 || cellProps.rowSpan === 0) return null

                        return (
                          <td
                            key={String(columnKeyOf(column, columnIndex))}
                            colSpan={cellProps.colSpan}
                            rowSpan={cellProps.rowSpan}
                            className={cn(
                              'ui-table-cell',
                              cellPadding,
                              ALIGN[column.align] || ALIGN.left,
                              column.ellipsis && 'max-w-[240px] truncate',
                              bordered && `border ${BORDER}`,
                              column.className,
                              (column.align === 'right' ||
                                column.numeric ||
                                /\b(prix|price|montant|amount|total|tarif|marge|taux|quantite|qty|date|heure|solde|ttc|ht|tva|currency)\b/i.test(
                                  String(column.dataIndex ?? column.key ?? '')
                                )) &&
                                'ui-numeric'
                            )}
                          >
                            {isDescriptor ? rendered.children : rendered}
                          </td>
                        )
                      })}
                    </tr>,
                    expandableEnabled && isExpanded ? (
                      <tr key={`${key}-expanded`} className="ui-table-expanded-row">
                        <td colSpan={totalColumnCount} className={cn('bg-[var(--gray-100)]', cellPadding)}>
                          {expandable.expandedRowRender(record, index, 0, isExpanded)}
                        </td>
                      </tr>
                    ) : null,
                  ]
                })
              )}
            </tbody>

            {summary ? <tfoot className="ui-table-summary">{summary(rows)}</tfoot> : null}
          </table>
        </div>
      </Spin>

      {footer ? <div className="ui-table-footer px-2 py-3 text-sm">{footer(dataSource)}</div> : null}

      {paginationEnabled && total > 0 ? (
        <div className="ui-table-pagination flex justify-end py-3">
          <Pagination
            {...resolvedPagination}
            total={resolvedPagination.total ?? total}
            onChange={changePage}
            onShowSizeChange={changePage}
          />
        </div>
      ) : null}
    </div>
  )
}

const Summary = ({ children, ...rest }) => <tr {...rest}>{children}</tr>
Summary.Row = ({ children, ...rest }) => <tr {...rest}>{children}</tr>
Summary.Cell = ({ children, index, colSpan, ...rest }) => (
  <td colSpan={colSpan} className="ui-table-cell p-2 font-medium" {...rest}>
    {children}
  </td>
)

Table.Summary = Summary
Table.Column = ({ children }) => children
Table.ColumnGroup = ({ children }) => children

export default Table
